// Copyright 2025, University of Maryland, All Rights Reserved
import { Card, CardContent } from "@/components/ui/card"
import { BookOpen, AlertCircle, CheckCircle, Loader2 } from "lucide-react"
import { type DatasourceBill } from '@/types/datasource-bill'
import { AmendmentSummaryCard } from './AmendmentSummaryCard'
import { AmendmentsByPageDisplay } from './AmendmentsByPageDisplay'
import { type ProcessedAmendmentData } from './types'

interface AmendmentsTabContentProps {
  bill: DatasourceBill
  selectedVersion: number | null
  processedAmendments: Record<number, ProcessedAmendmentData>
}

export function AmendmentsTabContent({ bill, selectedVersion, processedAmendments }: AmendmentsTabContentProps) {
  if (!selectedVersion) {
    return (
      <div className="text-center py-8">
        <BookOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium mb-2">No Version Selected</h3>
        <p className="text-muted-foreground">
          Select a version from the header to view amendment information.
        </p>
      </div>
    )
  }

  const selectedVersionData = bill.versions.find(v => v.version === selectedVersion)
  if (!selectedVersionData) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium mb-2">Version Not Found</h3>
        <p className="text-muted-foreground">
          The selected version could not be found.
        </p>
      </div>
    )
  }

  // Check if this version has amendments detected
  if (!selectedVersionData.amendments_detected) {
    return (
      <div className="text-center py-8">
        <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-4" />
        <h3 className="text-lg font-medium mb-2">No Amendments in Version {selectedVersion}</h3>
        <p className="text-muted-foreground">
          This version does not contain any amendments according to the analysis performed during text extraction.
        </p>
      </div>
    )
  }

  // Check if formatting data is available for versions with amendments
  if (!selectedVersionData.formatting_s3_key) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium mb-2">No Formatting Data Available</h3>
        <p className="text-muted-foreground">
          Formatting data is not available for Version {selectedVersion}.
        </p>
      </div>
    )
  }

  const amendmentData = processedAmendments[selectedVersion]
  if (!amendmentData) {
    return (
      <div className="text-center py-8">
        <Loader2 className="h-12 w-12 mx-auto text-muted-foreground mb-4 animate-spin" />
        <h3 className="text-lg font-medium mb-2">Loading Formatting Data</h3>
        <p className="text-muted-foreground">
          Fetching detailed formatting information for Version {selectedVersion}...
        </p>
      </div>
    )
  }

  if (amendmentData.summary.total_amendments === 0) {
    return (
      <div className="text-center py-8">
        <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-4" />
        <h3 className="text-lg font-medium mb-2">No Amendments in Version {selectedVersion}</h3>
        <p className="text-muted-foreground">
          This version does not contain any strikeouts or underlines indicating amendments.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Amendment Summary */}
      <AmendmentSummaryCard amendmentData={amendmentData} />

      {/* Page-by-Page Amendments */}
      <AmendmentsByPageDisplay amendments={amendmentData.amendments} />

      {/* Processing Info */}
      <Card>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="font-medium text-muted-foreground mb-2">Document Details</div>
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span>Bill Number:</span>
                  <span>{amendmentData.bill_number}</span>
                </div>
                <div className="flex justify-between">
                  <span>Session:</span>
                  <span>{amendmentData.session_code}</span>
                </div>
                <div className="flex justify-between">
                  <span>Version:</span>
                  <span>{amendmentData.version_number}</span>
                </div>
              </div>
            </div>
            <div>
              <div className="font-medium text-muted-foreground mb-2">Page Information</div>
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span>Total Pages:</span>
                  <span>{amendmentData.total_pages}</span>
                </div>
                <div className="flex justify-between">
                  <span>Pages with Amendments:</span>
                  <span>{amendmentData.summary.pages_with_amendments}</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
